import { type FC, type PropsWithChildren, useEffect, useState } from "react";

import { LocalizationProvider as LocalizationClientProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
// Определение локали `ru` должно быть зарегистрировано до updateLocale ниже,
// иначе moment создаст пустую локаль с одним полем months. Vite бандлит
// только дефолтную en, поэтому импорт явный (дубль импорта в i18n.ts не
// страшен — модуль исполняется один раз).
import "moment/locale/ru";
import { useTranslation } from "react-i18next";
import { MONTHS_GENITIVE } from "@/shared/utils/formatDate";

/**
 * Родительный падеж месяцев нужен русским пикерам («12 марта», а не
 * «12 март»). Правим именно определение локали `ru` через `updateLocale`, а не
 * делаем её активной: `moment.locale("ru", {...})` переключал бы глобальную
 * локаль moment на русскую независимо от языка интерфейса, и в английском
 * кабинете все пикеры и любой `moment().format("MMM")` показывали бы русские
 * месяцы.
 */
// На этом месте активная локаль moment уже «ru»: и импорт moment/locale/ru
// выше, и updateLocale ниже побочно делают затронутую локаль активной. Мы это
// не исправляем здесь, потому что правильную локаль всё равно выставляет
// эффект в самом провайдере, по i18n.language, ещё до первого рендера детей.
moment.updateLocale("ru", {
  months: MONTHS_GENITIVE,
});

/** Локали, для которых в бандл подключены определения moment (см. i18n.ts). */
const SUPPORTED_MOMENT_LOCALES = ["ru", "en"];

const toMomentLocale = (language: string): string => {
  const base = language.split("-")[0].toLowerCase();
  return SUPPORTED_MOMENT_LOCALES.includes(base) ? base : "en";
};

export const LocalizationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState(() => toMomentLocale(i18n.language));

  useEffect(() => {
    const next = toMomentLocale(i18n.language);
    // Глобальная локаль moment нужна не только пикерам: её читает любой
    // moment().format() без явного .locale(), в том числе внутри библиотек.
    // Код кабинета локаль задаёт явно, полагаться на глобальную не стоит.
    moment.locale(next);
    setLocale(next);
  }, [i18n.language]);

  return (
    <LocalizationClientProvider
      dateAdapter={AdapterMoment}
      dateLibInstance={moment}
      adapterLocale={locale}
    >
      {children}
    </LocalizationClientProvider>
  );
};
